import { useRouter } from 'next/router';

import { AdornedInput, FormField, FormHelp, Label, TextButton } from '@zillow/constellation-10';

import { useRentalManagerUserPropertyEditWebApiHandler_securityDeposit } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useScreenSize } from '../../../hooks';
import { useGetListingDetails } from '../../../hooks/queries/properties';
import { useFieldSave } from '../../../hooks/useFieldSave';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';
import numberUtils from '../../../utils/numberUtils';
import propertyUtils from '../../../utils/propertyUtils';

const inputName = 'securityDeposit';

const SecurityDepositFormField = ({
  isRelistingFieldset = false,
  showOneMonthsRentButton = true,
}: {
  isRelistingFieldset?: boolean;
  showOneMonthsRentButton?: boolean;
}) => {
  const router = useRouter();
  const { isScreenDesktop } = useScreenSize();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const floorPlans = listingDetails?.floorPlans || [];
  const initialSecurityDeposit = floorPlans[0]?.securityDeposit ?? null;
  const price = numberUtils.formatPriceFromApi(
    floorPlans[0]?.lowPrice?.toString(),
    floorPlans[0]?.highPrice?.toString(),
  );
  const isRoomForRent = propertyUtils.isRoomForRent({ listing: listingDetails });
  const formLabel = isRoomForRent ? `Renter’s security deposit` : 'Security deposit';

  const updateMutation = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_securityDeposit,
  });

  const securityDepositField = useFieldSave({
    fieldName: inputName,
    initialValue: initialSecurityDeposit,
    mutation: updateMutation,
    buildMutationVariables: (value) => {
      return {
        getParams: { listingAlias, securityDeposit: value },
      };
    },
  });

  const handleRequireOneMonthsRentClick = () => {
    securityDepositField.onChange(Number(listingDetails?.floorPlans[0]?.lowPrice ?? 0));
    securityDepositField.onBlur?.();
  };

  return (
    <>
      <FormField
        data-testid="SecurityDepositFormField"
        label={<Label size={isRelistingFieldset ? 'lg' : 'md'}>{formLabel}</Label>}
        error={!!securityDepositField.error}
        control={
          <AdornedInput
            size={isScreenDesktop ? 'lg' : 'md'}
            input={
              <AdornedInput.Input
                name={inputName}
                type="number"
                value={String(securityDepositField.value || '')}
                onChange={(e) => securityDepositField.onChange(e.target.value === '' ? null : Number(e.target.value))}
                onBlur={securityDepositField.onBlur}
              />
            }
            startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
          />
        }
        description={<FormHelp>{securityDepositField.error}</FormHelp>}
      />
      {price && showOneMonthsRentButton && (
        <TextButton
          tone="brand"
          onClick={handleRequireOneMonthsRentClick}
          data-testid="SecurityDepositFormField-SetDepositAsOneMonthsRentButton"
        >
          Set deposit as {numberUtils.asCurrency(Number(price))} (one month’s rent)
        </TextButton>
      )}
    </>
  );
};

export default SecurityDepositFormField;
